<template>
  <SidebarSettingsGroup
    v-if="showPositionControls || resizable || showTransformOptions || showFlipControls"
    title="Size & Placement"
    collapsible
    :element-type="element.type"
    data-test="graphics-positionSettings-sizeAndPlacement"
  >
    <div
      v-if="showPositionControls"
      class="settings-row sidebar-form-group"
      data-test="graphics-positionSettings-position"
    >
      <div class="settings-col-12">
        <label class="sidebar-form-label">Position</label>
      </div>
      <div class="settings-col-6">
        <input
          v-model.number="posX"
          type="number"
          :max="getCanvas.width"
          data-test="graphics-input-position-x"
          data-entity="graphics-positionSettings-setPositionX"
        >
        <label class="sidebar-input-sub-label">X</label>
      </div>
      <div class="settings-col-6">
        <input
          v-model.number="posY"
          type="number"
          :max="getCanvas.height"
          data-test="graphics-input-position-y"
          data-entity="graphics-positionSettings-setPositionY"
        >
        <label class="sidebar-input-sub-label">Y</label>
      </div>
    </div>

    <div
      v-if="resizable"
      class="settings-row sidebar-form-group has-aspect-lock"
      data-test="graphics-positionSettings-size"
    >
      <div class="settings-col-12">
        <label class="sidebar-form-label">Size</label>
      </div>
      <div class="settings-col-5">
        <!-- type logic to avoid console warnings as width changes for text layouts -->
        <input
          id="sizeWidth"
          v-model.number="width"
          :type="width === 'auto' ? 'text' : 'number'"
          min="0"
          data-test="graphics-positionSettings-setWidth"
        >
        <label class="sidebar-input-sub-label">Width</label>
      </div>
      <div class="settings-col-2 text-center">
        <button
          :disabled="restrictAspectLockTypes.includes(element.type)"
          tabindex="-1"
          class="btn-lock-aspect"
          :class="{ locked: aspectLock }"
          :data-balloon="getAspectLockBalloon()"
          data-balloon-pos="up"
          data-test="graphics-positionSettings-toggleAspectLock"
          data-entity="graphics-positionSettings-toggleAspectLock"
          @click.stop.prevent="toggleAspectLock"
        >
          <span :class="aspectLock ? 'fa-solid fa-lock' : 'fa-solid fa-unlock'" />
        </button>
      </div>
      <div class="settings-col-5">
        <!-- type logic to avoid console warnings as width changes for text layouts -->
        <input
          id="sizeHeight"
          v-model.number="height"
          :type="height === 'auto' ? 'text' : 'number'"
          min="0"
          data-test="graphics-positionSettings-setHeight"
          data-entity="graphics-positionSettings-setHeight"
        >
        <label class="sidebar-input-sub-label">Height</label>
      </div>
    </div>

    <template v-if="element.type !== 'progress-bar'">
      <div
        v-if="!editingMultiple"
        class="sidebar-form-group"
        data-test="graphics-positionSettings-snap"
      >
        <label class="sidebar-form-label">Snap to</label>
        <div class="sidebar__snap-controls">
          <button
            v-for="snap in snaps"
            :key="snap.snap"
            :data-balloon="snap.snap"
            :data-balloon-pos="snap.balloonPosition"
            class="btn btn-style-d btn-layer-position"
            :data-test="'graphics-positionSettings-snap-' + snap.snap"
            :data-entity="'graphics-positionSettings-snap-' + snap.snap"
            @click="placement(snap.snap)"
          >
            <span :class="snap.icon" />
          </button>
        </div>
      </div>

      <div
        v-if="!editingMultiple"
        class="sidebar-form-group"
        data-test="graphics-positionSettings-nudge"
      >
        <label class="sidebar-form-label">Nudge</label>
        <div class="sidebar__nudge-controls">
          <button
            class="btn btn-style-d btn-layer-position"
            data-test="graphics-positionSettings-nudge-left"
            data-entity="graphics-positionSettings-nudge-left"
            @click="nudge('left')"
          >
            <span class="fa-solid fa-arrow-left" />
          </button>
          <button
            class="btn btn-style-d btn-layer-position"
            data-test="graphics-positionSettings-nudge-down"
            data-entity="graphics-positionSettings-nudge-down"
          @click="nudge('down')"
          >
            <span class="fa-solid fa-arrow-down" />
          </button>
          <button
            class="btn btn-style-d btn-layer-position"
            data-test="graphics-positionSettings-nudge-up"
            data-entity="graphics-positionSettings-nudge-up"
          @click="nudge('up')"
          >
            <span class="fa-solid fa-arrow-up" />
          </button>
          <button
            class="btn btn-style-d btn-layer-position"
            data-test="graphics-positionSettings-nudge-right"
            data-entity="graphics-positionSettings-nudge-right"
          @click="nudge('right')"
          >
            <span class="fa-solid fa-arrow-right" />
          </button>
        </div>
      </div>

      <div
        v-if="showTransformOptions"
        class="settings-row settings-row-xs"
        data-test="graphics-positionSettings-transform"
      >
        <label class="sidebar-form-label">Transform</label>
        <div class="settings-col-4 settings-col-xs">
          <validation-field as="input"
            v-model.number="skewX"
            rules="required|between:-89,89"
            type="number"
            name="skewX"
            min="-89"
            max="89"
            :class="{ 'is-invalid': validationErrors['skewX'] }"
            data-test="graphics-positionSettings-setSkewX"
            data-entity="graphics-positionSettings-setSkewX"
          />
          <label class="sidebar-input-sub-label">Skew X</label>
        </div>
        <div class="settings-col-4 settings-col-xs">
          <validation-field as="input"
            v-model.number="skewY"
            rules="required|between:-89,89"
            type="number"
            name="skewY"
            min="-89"
            max="89"
            :class="{ 'is-invalid': validationErrors['skewY'] }"
            data-test="graphics-positionSettings-setSkewY"
            data-entity="graphics-positionSettings-setSkewY"
          />
          <label class="sidebar-input-sub-label">Skew Y</label>
        </div>
        <div class="settings-col-4 settings-col-xs">
          <validation-field as="input"
            v-model.number="rotate"
            rules="required|numeric|min:0|max:360"
            type="number"
            name="rotate"
            min="0"
            max="360"
            :class="{ 'is-invalid': validationErrors['rotate'] }"
            data-test="graphics-positionSettings-setRotate"
            data-entity="graphics-positionSettings-setRotate"
          />
          <label class="sidebar-input-sub-label">Rotate</label>
        </div>
      </div>
      <div
        v-if="showFlipControls"
        class="settings-row settings-row-xs"
        data-test="graphics-positionSettings-flip"
      >
        <label class="sidebar-form-label">Flip</label>
        <div class="settings-col-6 settings-col-xs">
          <button
            class="btn btn-sm btn-icon btn-style-b font-weight-bold flip-button"
            data-test="graphics-positionSettings-flipX"
            data-entity="graphics-positionSettings-flipX"
            @click="flipX = !flipX"
          >
            <span class="fak fa-lg fa-flip-horiz"></span>
            Horizontal
          </button>
        </div>

        <div class="settings-col-6 settings-col-sm">
          <button
            class="btn btn-sm btn-icon btn-style-b font-weight-bold flip-button"
            data-test="graphics-positionSettings-flipY"
            data-entity="graphics-positionSettings-flipY"
            @click="flipY = !flipY"
          >
            <span class="fak fa-lg fa-flip-vert"></span>
            Vertical
          </button>
        </div>
      </div>
    </template>
  </SidebarSettingsGroup>
</template>

<script>
/* eslint-disable no-underscore-dangle */
import { mapGetters, mapState } from 'vuex';
import { debounce } from 'lodash-es';
import SettingsMixins from '../../mixins/settings-mixins';
import EventBus from '../../utils/event-bus';
import toast from '../../utils/toasts';

export default {
  mixins: [SettingsMixins],
  props: {
    element: {
      type: Object,
      required: true,
    },
    areSameType: {
      type: Boolean,
      default: false,
    },
    areSameParent: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      restrictAspectLockTypes: ['qrcode'],
      snaps: [
        {
          snap: 'top-left',
          icon: 'fak fa-snap-to-top-left',
          balloonPosition: 'up',
        },
        {
          snap: 'center-top',
          icon: 'fak fa-snap-to-center-top',
          balloonPosition: 'up',
        },
        {
          snap: 'top-right',
          icon: 'fak fa-snap-to-top-right',
          balloonPosition: 'up',
        },

        {
          snap: 'center-left',
          icon: 'fak fa-snap-to-center-left',
          balloonPosition: 'up',
        },
        {
          snap: 'center',
          icon: 'fak fa-snap-to-center',
          balloonPosition: 'up-left',
        },
        {
          snap: 'center-right',
          icon: 'fak fa-snap-to-center-right',
          balloonPosition: 'up-right',
        },

        {
          snap: 'bottom-left',
          icon: 'fak fa-snap-to-bottom-left',
          balloonPosition: 'up-left',
        },
        {
          snap: 'center-bottom',
          icon: 'fak fa-snap-to-center-bottom',
          balloonPosition: 'up',
        },
        {
          snap: 'bottom-right',
          icon: 'fak fa-snap-to-bottom-right',
          balloonPosition: 'up',
        },
      ],
    };
  },

  computed: {
    ...mapState({
      elementGroupMap: (state) => state.graphicsTemplate.elements.elementGroupMap,
    }),
    ...mapGetters('graphicsTemplate', ['getCanvas']),
    posX: {
      get() {
        return this.getPropertyValue('styles.left');
      },
      set(v) {
        this.updateOneElementValue('styles.left', v);
      },
    },
    posY: {
      get() {
        return this.getPropertyValue('styles.top');
      },
      set(v) {
        this.updateOneElementValue('styles.top', v);
      },
    },
    width: {
      get() {
        return this.getPropertyValue('styles.width');
      },
      set(v) {
        this.debouncedUpdateSize('styles.width', v);
      },
    },
    height: {
      get() {
        return this.getPropertyValue('styles.height');
      },
      set(v) {
        this.debouncedUpdateSize('styles.height', v);
      },
    },
    skewX: {
      get() {
        return this.getPropertyValue('skewX', { fallbackValue: 0 });
      },
      set(v) {
        this.updateOneElementValue('skewX', v);
      },
    },
    skewY: {
      get() {
        return this.getPropertyValue('skewY', { fallbackValue: 0 });
      },
      set(v) {
        this.updateOneElementValue('skewY', v);
      },
    },
    rotate: {
      get() {
        return this.getPropertyValue('rotate', { fallbackValue: 0 });
      },
      set(v) {
        this.updateOneElementValue('rotate', v);
      },
    },
    flipX: {
      get() {
        return this.getPropertyValue('flipX');
      },
      set(v) {
        this.updateOneElementValue('flipX', v);
      },
    },
    flipY: {
      get() {
        return this.getPropertyValue('flipY');
      },
      set(v) {
        this.updateOneElementValue('flipY', v);
      },
    },
    aspectLock: {
      get() {
        return this.getPropertyValue('aspectLock');
      },
      set(v) {
        this.updateOneElementValue('aspectLock', v);
      },
    },

    showPositionControls() {
      return !this.editingMultiple;
      // saving in case we want position controls for multiple elements later
      // return !this.editingMultiple || (this.editingMultiple && this.areSameParent);
    },

    resizable() {
      if (this.editingMultiple && !this.areSameType) return false;

      // Only resizable if element is NOT a group
      return !['group', 'data-list', 'shoppable'].includes(this.element.type);
    },

    showTransformOptions() {
      if (this.editingMultiple && !this.areSameType) return false;

      return [
        'rectangle',
        'triangle',
        'image',
        'text',
        'date',
        'timer',
      ].includes(this.element.type);
    },

    showFlipControls() {
      if (this.editingMultiple && !this.areSameType) return false;
      return ['image'].includes(this.element.type);
    },

    groupMap() {
      return this.elementGroupMap?.[this.element.id];
    },

    noOffCanvasPosition() {
      return this.groupMap?.some((c) => (
        this.getElementById(c).noOffCanvasPosition
      )) || this.element.noOffCanvasPosition;
    },
  },

  watch: {
    posX(v) {
      if (!this.noOffCanvasPosition) return;

      const parentEl = this.getElementById(this.parent);
      const parentX = parentEl?.styles?.left;
      const canvasWidth = this.getCanvas.width;
      const elementWidth = this.element.styles.width;

      const leftLimit = this.parent !== 'root' ? -parentX : 0;
      const rightLimit = this.parent !== 'root' ? canvasWidth - elementWidth - parentX : canvasWidth - elementWidth;

      // Ensure the position is within bounds
      if (v < leftLimit) {
        this.posX = leftLimit;
      } else if (v > rightLimit) {
        this.posX = rightLimit;
      }

      if (v < 0 || v > rightLimit) {
        this.showOffCanvasWarning();
      }
    },

    posY(v) {
      if (!this.noOffCanvasPosition) return;

      const parentEl = this.getElementById(this.parent);
      const parentY = parentEl?.styles?.top;
      const canvasHeight = this.getCanvas.height;
      const elementHeight = this.element.styles.height;

      const topLimit = this.parent !== 'root' ? -parentY : 0;
      const bottomLimit = this.parent !== 'root' ? canvasHeight - elementHeight - parentY : canvasHeight - elementHeight;

      // Ensure the position is within bounds
      if (v < topLimit) {
        this.posY = topLimit;
      } else if (v > bottomLimit) {
        this.posY = bottomLimit;
      }

      if (v < 0 || v > bottomLimit) {
        this.showOffCanvasWarning();
      }
    },
  },

  created() {
    this.debouncedUpdateSize = debounce(this.updateSize, 500);
  },

  methods: {
    toggleAspectLock() {
      this.updateOneElementValue('aspectLock', !this.aspectLock);
    },

    placement(snap) {
      EventBus.emit('snap-position', snap);
    },

    updateSize(propKey, propValue, id = this.element.id, options = {}, skipCheck = false) {
      if (this.aspectLock) {
        // this matches how multipleElementEdit in settings-mixins works
        // we just have to do extra things with AR so we can't use that directly
        if (this.editingMultiple && !skipCheck) {
          this.selectedElements.forEach((elementId, index) => {
            const opts = {
              ...options,
              blacklist: index === this.selectedElements.length - 1,
            };
            this.updateSize(propKey, propValue, elementId, opts, true);
          });
        } else {
          const element = this.getElementById(id);
          const w = element.styles.width;
          const h = element.styles.height;
          const ar = w / h;
          const newValue = propValue;
          let adjustedKey;
          let adjustedValue;

          if (propKey === 'styles.width') {
            adjustedKey = 'styles.height';
            adjustedValue = Math.round(newValue / ar);
          } else {
            adjustedKey = 'styles.width';
            adjustedValue = Math.round(newValue * ar);
          }
          this.updateManyElementValues({
            [propKey]: newValue,
            [adjustedKey]: adjustedValue,
          }, id, options, true);
        }
      } else {
        this.updateOneElementValue(propKey, propValue);
      }
    },

    nudge(direction) {
      switch (direction) {
        case 'left':
          this.decrementPosition('left');
          break;
        case 'up':
          this.decrementPosition('top');
          break;
        case 'right':
          this.incrementPosition('left');
          break;
        case 'down':
          this.incrementPosition('top');
          break;
        default:
          break;
      }

      if (this.parent !== 'root') {
        this.onMoveOrResize({
          value: {
            groupId: this.parent,
          },
        });
      }
    },

    showOffCanvasWarning() {
      toast.makeToast({
        heading: 'Information',
        icon: 'info',
        hideAfter: 4000,
        stack: false,
        text: this.groupMap ? 'This element is required or contains required child elements.' : 'This element cannot be placed off canvas.',
        loaderBg: '#00C4DD',
        bgColor: '#2288A0',
        textColor: '#FFFFFF',
      });
    },

    getAspectLockBalloon() {
      if (this.restrictAspectLockTypes.includes(this.element.type)) {
        return 'Aspect lock change restricted';
      }
      return this.aspectLock ? 'Aspect ratio locked' : 'Freeform';
    },
  },
};
</script>
<style lang="scss" scoped>
.sidebar__nudge-controls {
  display: flex;
  justify-content: space-between;
  gap: 5px;
}
.flip-button {
  font-size: 13px;
}
</style>
